import React from 'react';
import translate from '@data/translations';
import useStore from '@data/state/zustand';

// Import CISU codebooks
import cisuMunicipalities from '@common/eGuestsData/cisuMunicipalities.json';
import cisuPlaces from '@common/eGuestsData/cisuPlaces.json';

interface CisuDataTabProps {
    customer: any;
    setCustomer: (customer: any) => void;
}

interface Municipality {
    name: string;
    code: string;
    entity: string;
}

interface Place {
    name: string;
    code: string;
    municipality: string;
}

const CisuDataTab = (props: CisuDataTabProps) => {
    const { customer, setCustomer } = props;
    const cisuSettings = useStore((appState: any) => appState?.cisuSettings);
    
    // Don't render if CISU is not enabled
    if (!cisuSettings?.cisuEnabled) {
        return null;
    }

    // Initialize cisu object in params if not exists
    const cisuData = customer?.params?.cisu || {};

    const handleCisuChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        const updatedCustomer = {
            ...customer,
            params: {
                ...customer.params,
                cisu: {
                    ...cisuData,
                    [name]: value || null
                }
            }
        };
        setCustomer(updatedCustomer);
    };

    // Find selected municipality name for place filtering
    const selectedMunicipality = cisuData.residenceMunicipalityCode
        ? (cisuMunicipalities as Municipality[]).find((m: Municipality) => m.code === cisuData.residenceMunicipalityCode)
        : null;

    // Filter places by selected municipality NAME (places use name, not code)
    const filteredPlaces = selectedMunicipality
        ? (cisuPlaces as Place[]).filter((p: Place) => p.municipality === selectedMunicipality.name)
        : [];

    // When municipality changes, reset place
    const handleMunicipalityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { value } = e.target;
        const updatedCustomer = {
            ...customer,
            params: {
                ...customer.params,
                cisu: {
                    ...cisuData,
                    residenceMunicipalityCode: value || null,
                    residencePlaceCode: null // Reset place when municipality changes
                }
            }
        };
        setCustomer(updatedCustomer);
    };

    // Check if customer is domestic (BiH citizen)
    // nationality uses citizenshipCode.json num_code ("70"), country uses countryCodes.json country-code ("070")
    const isDomestic = customer?.nationality === '70' || customer?.country === '070';

    return (
        <div className="mt-12p p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
            <div className="display-flex align-items-center mb-12p">
                <i className="fa fa-id-card mr-8p" style={{ color: '#007bff' }}></i>
                <span className="text-bold">{translate('CISU Data')} (Republika Srpska)</span>
                <span className="ml-8p text-muted" style={{ fontSize: '0.85em' }}>
                    - {translate('Optional')}
                </span>
            </div>

            {/* JMBG - only for domestic guests */}
            {isDomestic && (
                <div className="display-flex mt-12p">
                    <div className="flex mr-8p">
                        <label className="text-bold ml-4p" htmlFor="jmbg">
                            {translate('JMBG')}:
                            <span className="text-muted ml-4p" style={{ fontSize: '0.85em' }}>
                                ({translate('for BiH citizens')})
                            </span>
                        </label>
                        <input
                            autoComplete="off"
                            name="jmbg"
                            placeholder="1234567890123"
                            value={cisuData.jmbg || ''}
                            onChange={handleCisuChange}
                            type="text"
                            maxLength={13}
                            className="input input-modal"
                        />
                    </div>
                    <div className="flex ml-8p">
                        <label className="text-bold ml-4p" htmlFor="birthMunicipality">
                            {translate('Birth Municipality')}:
                        </label>
                        <input
                            autoComplete="off"
                            name="birthMunicipality"
                            placeholder={translate('e.g. Banja Luka')}
                            value={cisuData.birthMunicipality || ''}
                            onChange={handleCisuChange}
                            type="text"
                            className="input input-modal"
                        />
                    </div>
                </div>
            )}

            {/* Residence - Municipality and Place - only for domestic guests */}
            {isDomestic && (
            <div className="display-flex mt-12p">
                <div className="flex mr-8p">
                    <label className="text-bold ml-4p" htmlFor="residenceMunicipalityCode">
                        {translate('Residence Municipality')}:
                    </label>
                    <select
                        name="residenceMunicipalityCode"
                        className="input input-modal"
                        value={cisuData.residenceMunicipalityCode || ''}
                        onChange={handleMunicipalityChange}
                    >
                        <option value="">{translate('Select municipality')}</option>
                        {(cisuMunicipalities as Municipality[]).map((m: Municipality, index: number) => (
                            <option key={'mun'+index+'_'+m.code} value={m.code}>
                                {m.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="flex ml-8p">
                    <label className="text-bold ml-4p" htmlFor="residencePlaceCode">
                        {translate('Residence Place')}:
                    </label>
                    <select
                        name="residencePlaceCode"
                        className="input input-modal"
                        value={cisuData.residencePlaceCode || ''}
                        onChange={handleCisuChange}
                        disabled={!cisuData.residenceMunicipalityCode}
                    >
                        <option value="">{translate('Select place')}</option>
                        {filteredPlaces.map((p: Place, index: number) => (
                            <option key={'place'+index+'_'+p.code} value={p.code}>
                                {p.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            )}

            {/* Identity card issuer + expiry date - for foreign guests */}
            {!isDomestic && (
                <div className="display-flex mt-12p">
                    <div className="flex mr-8p">
                        <label className="text-bold ml-4p" htmlFor="identityCardIssuer">
                            {translate('Identity Card Issuer')}:
                            <span className="text-muted ml-4p" style={{ fontSize: '0.85em' }}>
                                ({translate('for foreign citizens')})
                            </span>
                        </label>
                        <input
                            autoComplete="off"
                            name="identityCardIssuer"
                            placeholder={translate('e.g. MUP BiH')}
                            value={cisuData.identityCardIssuer || ''}
                            onChange={handleCisuChange}
                            type="text"
                            className="input input-modal"
                        />
                    </div>
                    <div className="flex ml-8p">
                        <label className="text-bold ml-4p" htmlFor="identityCardExpiryDate">
                            {translate('ID Card Expiry Date')}:
                        </label>
                        <input
                            autoComplete="off"
                            name="identityCardExpiryDate"
                            value={cisuData.identityCardExpiryDate || ''}
                            onChange={handleCisuChange}
                            type="date"
                            className="input input-modal"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default CisuDataTab;